import { useEffect } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ArrowLeft, Check } from 'lucide-react';

const extras = [
  {
    id: 'none',
    label: 'Ingen Ekstra',
    price: 'Inkludert',
    priceNote: 'Standard',
    img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/f187d11d3_HvittHarvia.png',
    description: 'Badstuen leveres med standard Harvia tilbehørssett – inkluderer bøtte, øse og termometer. Alt du trenger for en god start.',
    pros: ['Harvia bøtte og øse', 'Termometer inkludert', 'Komplett for bruk fra dag 1', 'Ingen ekstra kostnad'],
  },
  {
    id: 'towel-rack',
    label: 'Håndklestativ',
    price: '+3 000 kr',
    priceNote: 'Tillegg',
    img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/32ed83a6b_TerrakottaSET.png',
    description: 'Elegant håndklestativ i rustfritt stål montert ved inngangen. Praktisk oppbevaring for håndklær og badekåper rett utenfor badstuen.',
    pros: ['Rustfritt stål — holdbart', 'Montert ved inngang', 'Plass til håndklær og badekåper', 'Stilren og praktisk'],
  },
  {
    id: 'backrest',
    label: 'Ryggstøtte',
    price: '+5 000 kr',
    priceNote: 'Tillegg',
    img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/1fc3e047b_Brown.png',
    description: 'Ergonomisk ryggstøtte i tre for økt komfort på benken. Justerbar vinkel og enkel montering – perfekt for lengre badstuøkter.',
    pros: ['Ergonomisk utformet', 'Justerbar vinkel', 'Enkel montering og fjerning', 'Perfekt for lengre økter'],
  },
  {
    id: 'full-package',
    label: 'Komplett Pakke',
    price: '+12 000 kr',
    priceNote: 'Best verdi',
    img: 'https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/f187d11d3_HvittHarvia.png',
    description: 'Alt-i-ett pakke med ryggstøtte, håndklestativ, premium tretilbehørssett og innrammede badstuveiledninger. Best verdi for den komplette opplevelsen.',
    pros: ['Ryggstøtte inkludert', 'Håndklestativ inkludert', 'Premium Harvia tilbehørssett', 'Innrammede badstuveiledninger'],
  },
];

export default function InnvendigEkstra() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
        className="relative h-[500px] overflow-hidden">
        <img src="https://media.base44.com/images/public/69cd72579c1c23d07f425ea3/1fc3e047b_Brown.png"
          alt="Ekstra" className="absolute inset-0 w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/50"></div>
        <div className="absolute inset-0 flex flex-col justify-end p-12 z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-sm tracking-widest font-body uppercase text-white/60 mb-4">Innvendig materialer · Artikkel 6</p>
            <h1 className="font-heading text-6xl md:text-7xl font-light leading-[1.0] text-white mb-4">Ekstra</h1>
            <p className="font-body text-white/80 text-xl max-w-2xl leading-relaxed">
              Tillegg som gjør badstuopplevelsen komplett
            </p>
          </motion.div>
        </div>
      </motion.div>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mb-16 max-w-3xl">
          <h2 className="font-heading text-4xl font-light text-[#2a1810] mb-6">Hvilke ekstrautstyr kan du velge?</h2>
          <p className="font-body text-lg text-foreground/70 leading-relaxed">
            Her er en kortfattet oversikt over tillegg og tilbehør du kan velge mellom. Fra standard Harvia-sett til komplett pakke med alt du trenger.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {extras.map((item, idx) => (
            <motion.div key={item.id}
              initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: idx * 0.1 }}
              whileHover={{ y: -6 }}
              className={`bg-white border rounded-[12px] overflow-hidden hover:shadow-xl transition-all ${item.id === 'full-package' ? 'border-[#2d5a3d] shadow-md' : 'border-border/20 hover:border-[#2d5a3d]'}`}>
              {item.id === 'full-package' && (
                <div className="bg-[#2d5a3d] text-white text-center py-2 font-body text-xs uppercase tracking-widest">
                  ⭐ Anbefalt — Best verdi
                </div>
              )}
              <div className="relative h-80 overflow-hidden bg-secondary/20 flex items-center justify-center">
                <img src={item.img} alt={item.label} className="h-full w-full object-contain p-6" />
                <div className="absolute top-4 right-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-body font-medium ${item.priceNote === 'Standard' ? 'bg-[#2d5a3d] text-white' : item.priceNote === 'Best verdi' ? 'bg-[#2d5a3d] text-white' : 'bg-white/90 text-[#2a1810]'}`}>
                    {item.price}
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-heading text-xl font-light text-[#2a1810] mb-3">{item.label}</h3>
                <p className="font-body text-foreground/70 leading-relaxed mb-4 text-base">{item.description}</p>
                <div className="space-y-1">
                  {item.pros.map((pro, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Check size={12} className="text-[#2d5a3d] flex-shrink-0" />
                      <span className="font-body text-base text-foreground/60">{pro}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="bg-[#2d5a3d] text-white rounded-[12px] p-10 mb-12">
          <h3 className="font-heading text-2xl font-light mb-4">Klar til å konfigurere din badstue?</h3>
          <p className="font-body text-white/80 leading-relaxed mb-6">
            Nå som du kjenner alle valgmulighetene, er du klar for konfiguratoren. Sett sammen din drømmebadstue og send forespørselen.
          </p>
          <a href="/sauna-ly" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#2a1810] font-body tracking-widest text-sm font-semibold rounded-[4px] hover:bg-white/90 transition-all">
            Se badstuemodeller
          </a>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <a href="/innvendig-belysning" className="inline-flex items-center gap-2 px-6 py-3 border border-border text-foreground font-body text-sm tracking-widest hover:bg-foreground hover:text-background transition-all rounded-[4px]">
            <ArrowLeft size={16} /> Forrige: Belysning
          </a>
          <a href="/laer-mer-handverk" className="inline-flex items-center gap-2 px-8 py-4 bg-[#2a1810] text-white font-body tracking-widest text-sm font-semibold rounded-[4px] hover:bg-[#2a1810]/90 transition-all">
            Tilbake til Lær Mer
          </a>
        </div>
      </div>
      <Footer />
    </div>
  );
}